import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    List,
    ListItem,
    ListItemText,
    Container,
    Button,
    ButtonGroup, // Para los botones de navegación
    IconButton // Para iconos en los botones
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from 'notistack';
import api from '../api/axios'; // Asumiendo que 'api' es tu instancia de Axios

import { jsPDF } from "jspdf";
import "jspdf-autotable";
import autoTable from 'jspdf-autotable';
import { format, addMonths, subMonths, startOfMonth  } from 'date-fns';
import { es } from "date-fns/locale";

function ProfilePage() {
    // Asumiendo que useAuth proporciona isAuthenticated y loading
    const { isAuthenticated, loading: authLoading } = useAuth(); // Descomenta si usas useAuth
    // Si no usas useAuth, puedes manejar un isAuthenticated/authLoading básico para el ejemplo
    const [allReservas, setAllReservas] = useState([]); // Todas las reservas fetched
const [displayedMonth, setDisplayedMonth] = useState(() => startOfMonth(new Date()));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        const fetchReservas = async () => {
            if (!isAuthenticated) return;

            setLoading(true);
            try {
                const year = displayedMonth.getFullYear();
                const month = displayedMonth.getMonth() + 1;

                const response = await api.get('/Reservas/por-mes', {
                    params: { year, month }
                });

                setAllReservas(response.data);
            } catch (err) {
                setError('Error al cargar reservas');
            } finally {
                setLoading(false);
            }
        };

        fetchReservas();
    }, [displayedMonth, isAuthenticated]);

    // Filtra las reservas para el mes actualmente visible
    const filteredReservas = [...allReservas].sort(
        (a, b) => new Date(a.fechaReserva) - new Date(b.fechaReserva)
    );


const handlePreviousMonth = () => {
  setDisplayedMonth(prev => startOfMonth(subMonths(prev, 1)));
};

const handleNextMonth = () => {
  setDisplayedMonth(prev => startOfMonth(addMonths(prev, 1)));
};


    const getTurnoText = (turno) => {
        switch (turno) {
            case "MedioDia":
                return 'Medio día';
            case "Noche":
                return 'Noche';
            default:
                return 'Desconocido';
        }
    };

    if (authLoading || !isAuthenticated) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Cargando...</Typography>
            </Box>
        );
    }

    const buscarTipoReserva = (tipoReservaBase) => {
        switch (tipoReservaBase) {
            case 1:
                return 'Salon con parrillero y vajilla';
            case 2:
                return 'Parrillero con vajilla';
            case 3:
                return 'Salon o parrillero sin vajilla';
            default:
                return 'Desconocido';
        }
    }

    const generatePDF = async () => {
        const doc = new jsPDF();

        // Filtrar reservas del mes mostrado
        const reservasDelMes = allReservas;

        if (reservasDelMes.length === 0) {
            enqueueSnackbar("No hay reservas para este mes.", { variant: "warning" });
            return;
        }

        // Título del documento
        const mesAnio = format(displayedMonth, "MMMM yyyy", { locale: es });
        doc.setFontSize(16);
        doc.text(`Reporte de Reservas - ${mesAnio}`, 20, 15);

        // Agrupar por apartamento o bloqueos
        const reservasPorApto = reservasDelMes.reduce((acc, reserva) => {
            const apto = reserva.esBloqueoAdmin ? "Bloqueo Administrativo" : reserva.numeroApartamento;
            if (!acc[apto]) acc[apto] = [];
            acc[apto].push(reserva);
            return acc;
        }, {});

        let yOffset = 30;

        Object.keys(reservasPorApto).forEach(apto => {
            const reservas = reservasPorApto[apto];

            doc.setFontSize(14);
            doc.text(`Apartamento: ${apto}`, 20, yOffset);
            yOffset += 10;

            const tableColumn = ["Fecha", "Turno", "Opciones Adicionales", "Precio Total"];
            const tableRows = reservas.map(reserva => [
                format(new Date(reserva.fechaReserva), "PPP", { locale: es }),
                getTurnoText(reserva.turno),
                reserva.opcionesAdicionales?.length > 0
                    ? reserva.opcionesAdicionales.map(o => `${o.nombre} (${o.cantidad})`).join(", ")
                    : "Ninguna",
                `$${reserva.precioTotal.toFixed(2)}`
            ]);

            autoTable(doc, {
                startY: yOffset,
                head: [tableColumn],
                body: tableRows,
                margin: { top: 10, left: 20, right: 20 }
            });

            yOffset = doc.lastAutoTable.finalY + 10;
        });

        // Guardar con el nombre personalizado
        doc.save(`Reporte_Reservas_${mesAnio}.pdf`);
    };


    return (
        <Container component="main" maxWidth="md" sx={{ my: 12 }}>
            <Typography variant="h4" component="h1" align="center" gutterBottom sx={{ mb: 4, color: 'text.primary' }}>
                Reservas
            </Typography>
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2, color: 'text.secondary' }}>Cargando tus reservas...</Typography>
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ my: 2, textAlign: 'center' }}>
                    {error}
                </Alert>
            )}

            {!loading && !error && (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
                        <ButtonGroup variant="outlined" aria-label="Navegación de meses">
                            <Button color='secondary' onClick={handlePreviousMonth} startIcon={<ArrowBackIosIcon fontSize="small" />}>
                                Anterior
                            </Button>
                            <Button disabled>
                                {format(displayedMonth, 'MMMM yyyy', { locale: es })}
                            </Button>
                            <Button color='secondary' onClick={handleNextMonth} endIcon={<ArrowForwardIosIcon fontSize="small" />}>
                                Siguiente
                            </Button>
                        </ButtonGroup>
                    </Box>

                    <Button onClick={generatePDF} variant="contained" color="primary" sx={{ mb: 2 }}>
                        Generar PDF
                    </Button>

                    <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        {filteredReservas.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                    No tienes reservas para {format(displayedMonth, 'MMMM yyyy', { locale: es })}.
                                </Typography>
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }} aria-label="historial de reservas">
                                    <TableHead sx={{ bgcolor: '#104d7bff' }}>
                                        <TableRow>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fecha</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tipo Reserva</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Turno</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Apartamento</TableCell> {/* <-- Nueva columna aquí */}
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Opciones Adicionales</TableCell>
                                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Precio Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredReservas.map((reserva) => (
                                            <TableRow
                                                key={reserva.id}
                                                sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'action.hover' } }}
                                            >
                                                <TableCell component="th" scope="row">
                                                    {format(new Date(reserva.fechaReserva), 'PPP', { locale: es })}
                                                </TableCell>
                                                <TableCell>{buscarTipoReserva(reserva.tipoReservaBase)}</TableCell>
                                                <TableCell>{getTurnoText(reserva.turno)}</TableCell>

                                                {/* <-- Lógica condicional aquí para la nueva celda */}
                                                <TableCell>
                                                    {reserva.esBloqueoAdmin ? '' : reserva.numeroApartamento}
                                                </TableCell>

                                                <TableCell>
                                                    {reserva.opcionesAdicionales && reserva.opcionesAdicionales.length > 0 ? (
                                                        <List dense disablePadding>
                                                            {reserva.opcionesAdicionales.map((opcion, index) => (
                                                                <ListItem key={index} disableGutters>
                                                                    <ListItemText
                                                                        primary={`${opcion.nombre} (${opcion.cantidad} unid.)`}
                                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">Ninguna</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                        ${reserva.precioTotal.toFixed(2)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </>
            )}
        </Container>
    );
}
export default ProfilePage;